import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  SimpleGrid,
  Skeleton,
  Alert,
  AlertIcon,
  AlertDescription,
  FormLabel,
  FormControl,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Select,
  Divider,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  useColorModeValue,
  useToast,
  Icon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Tooltip,
  Code,
  Input,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/react';
import {
  MdSmartToy,
  MdSave,
  MdOpenInNew,
  MdSearch,
  MdAdd,
  MdDelete,
  MdChevronRight,
  MdAutoFixHigh,
  MdBarChart,
  MdChatBubbleOutline,
  MdPerson,
} from 'react-icons/md';
import { adminApi } from '../../../services/adminApi';
import { AutoResizeTextarea } from '../../../components/shared/AutoResizeTextarea';
import { toUserMessage } from '../../../lib/apiError';

// ─── Types ────────────────────────────────────────────────────────────────────

type AiConfig = Awaited<ReturnType<typeof adminApi.ai.getConfig>>;
type Conversation = Awaited<ReturnType<typeof adminApi.ai.listConversations>>['conversations'][0];
type ConversationDetail = Awaited<ReturnType<typeof adminApi.ai.getConversation>>;
type DailyUsage = Awaited<ReturnType<typeof adminApi.ai.getUsage>>;

interface TenantOption {
  id: string;
  name: string;
}

// ─── helpers ─────────────────────────────────────────────────────────────────

function formatDate(iso: string) {
  return new Date(iso).toLocaleString();
}

function formatTokens(n: number) {
  return n.toLocaleString();
}

function formatUsd(value: number) {
  return `$${value.toFixed(6)}`;
}

const NORMALIZER_MODEL_COST_PROFILES: Record<
  string,
  {
    perInferenceUsd: number;
    reliabilityNote: string;
  }
> = {
  "deepseek-ai/DeepSeek-V3.1": {
    perInferenceUsd: 0.0001134,
    reliabilityNote: "100/100 benchmark pass",
  },
  "Qwen/Qwen2.5-72B-Instruct": {
    perInferenceUsd: 0.00025787,
    reliabilityNote: "100/100 benchmark pass",
  },
  "Qwen/Qwen3-32B": {
    perInferenceUsd: 0.0000898,
    reliabilityNote: "90/100 benchmark pass",
  },
};

// ─── TenantSelector ───────────────────────────────────────────────────────────

interface TenantSelectorProps {
  value: TenantOption | null;
  onChange: (t: TenantOption | null) => void;
}

const TenantSelector: React.FC<TenantSelectorProps> = ({ value, onChange }) => {
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const muted = useColorModeValue('gray.500', 'gray.400');
  const bg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const activeBg = useColorModeValue('blue.50', 'blue.900');

  useEffect(() => {
    adminApi.tenants.list({ limit: 100, sortBy: 'name', sortOrder: 'asc' })
      .then(res => setTenants(res.data.map(t => ({ id: t.id, name: t.name }))))
      .catch(err => setFetchError(toUserMessage(err, "We couldn't load tenants. Please try again.")))
      .finally(() => setLoading(false));
  }, []);

  const filtered = tenants.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box>
      <FormLabel fontSize="sm">Tenant</FormLabel>
      <InputGroup size="sm" mb={2}>
        <InputLeftElement pointerEvents="none">
          <Icon as={MdSearch} color={muted} />
        </InputLeftElement>
        <Input
          pl={8}
          placeholder="Search tenant..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </InputGroup>

      <Box
        border="1px"
        borderColor={border}
        borderRadius="md"
        maxH="200px"
        overflowY="auto"
        bg={bg}
      >
        {loading ? (
          <VStack p={2} spacing={1}>
            {[1,2,3].map(i => <Skeleton key={i} h="30px" w="full" borderRadius="md" />)}
          </VStack>
        ) : fetchError ? (
          <Text fontSize="sm" color="red.400" p={3}>{fetchError}</Text>
        ) : filtered.length === 0 ? (
          <Text fontSize="sm" color={muted} p={3}>No results</Text>
        ) : (
          filtered.map(t => (
            <Box
              key={t.id}
              px={3}
              py={2}
              cursor="pointer"
              bg={value?.id === t.id ? activeBg : 'transparent'}
              _hover={{ bg: value?.id === t.id ? activeBg : hoverBg }}
              onClick={() => onChange(t)}
            >
              <Text fontSize="sm" fontWeight={value?.id === t.id ? 'semibold' : 'normal'}>
                {t.name}
              </Text>
              <Text fontSize="xs" color={muted}>{t.id}</Text>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

// ─── StatCard ─────────────────────────────────────────────────────────────────

const StatCard: React.FC<{ label: string; value: string | number; color?: string }> = ({
  label, value, color = 'blue.500',
}) => {
  const bg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  const muted = useColorModeValue('gray.500', 'gray.400');

  return (
    <Box bg={bg} border="1px" borderColor={border} borderRadius="lg" p={4} textAlign="center">
      <Text fontSize="2xl" fontWeight="bold" color={color}>{value}</Text>
      <Text fontSize="xs" color={muted}>{label}</Text>
    </Box>
  );
};

// ─── ConfigTab ────────────────────────────────────────────────────────────────

const EMPTY_NEW_AGENT = { id: '', label: '', systemPrompt: '', defaultPrompt: '' };

const ConfigTab: React.FC = () => {
  const [config, setConfig] = useState<AiConfig | null>(null);
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingNormalizer, setSavingNormalizer] = useState(false);
  const [savingAgent, setSavingAgent] = useState<string | null>(null);
  const [deletingAgent, setDeletingAgent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [defaultModel, setDefaultModel] = useState('');
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(1500);
  const [dailyCap, setDailyCap] = useState(50000);
  const [merchantNormalizationModel, setMerchantNormalizationModel] = useState('deepseek-ai/DeepSeek-V3.1');
  const [merchantNormalizationPrompt, setMerchantNormalizationPrompt] = useState('');
  const [estimatedTransactionsPerDay, setEstimatedTransactionsPerDay] = useState(10000);
  const [estimatedFallbackRatePct, setEstimatedFallbackRatePct] = useState(15);
  const [estimatedDaysPerMonth, setEstimatedDaysPerMonth] = useState(30);
  const [agentEdits, setAgentEdits] = useState<Record<string, { defaultPrompt: string; systemPrompt: string }>>({});
  const [agentSearch, setAgentSearch] = useState('');
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);

  // New agent modal
  const { isOpen: isNewAgentOpen, onOpen: openNewAgent, onClose: closeNewAgent } = useDisclosure();
  const [newAgent, setNewAgent] = useState(EMPTY_NEW_AGENT);
  const [creatingAgent, setCreatingAgent] = useState(false);
  const [newAgentError, setNewAgentError] = useState<string | null>(null);

  const muted = useColorModeValue('gray.500', 'gray.400');
  const bg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  const cardHeaderBg = useColorModeValue('purple.50', 'purple.900');
  const cardHeaderBorder = useColorModeValue('purple.100', 'purple.700');
  const cardIconBg = useColorModeValue('purple.100', 'purple.800');
  const cardIconColor = useColorModeValue('purple.600', 'purple.300');
  const neutralSectionBg = useColorModeValue('white', 'gray.800');
  const neutralSectionBorder = useColorModeValue('gray.200', 'gray.700');
  const toast = useToast();

  const selectedCostProfile = useMemo(
    () => NORMALIZER_MODEL_COST_PROFILES[merchantNormalizationModel] ?? null,
    [merchantNormalizationModel],
  );

  const costEstimate = useMemo(() => {
    const perInference = selectedCostProfile?.perInferenceUsd ?? 0;
    const fallbackRate = Math.max(0, Math.min(100, estimatedFallbackRatePct)) / 100;
    const aiCallsPerDay = Math.round(estimatedTransactionsPerDay * fallbackRate);
    const aiCallsPerMonth = aiCallsPerDay * estimatedDaysPerMonth;

    return {
      perInference,
      aiCallsPerDay,
      aiCallsPerMonth,
      per10k: perInference * 10000,
      per100k: perInference * 100000,
      per1m: perInference * 1000000,
      perDay: perInference * aiCallsPerDay,
      perMonth: perInference * aiCallsPerMonth,
    };
  }, [
    selectedCostProfile,
    estimatedFallbackRatePct,
    estimatedTransactionsPerDay,
    estimatedDaysPerMonth,
  ]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cfg, models] = await Promise.all([
        adminApi.ai.getConfig(),
        adminApi.ai.getModels(),
      ]);
      setConfig(cfg);
      setAvailableModels(models);
      setDefaultModel(cfg.defaultModel);
      setTemperature(cfg.temperature);
      setMaxTokens(cfg.maxTokens);
      setDailyCap(cfg.dailyTokenCapPerTenant);
      setMerchantNormalizationModel(cfg.merchantNormalizationModel || 'deepseek-ai/DeepSeek-V3.1');
      setMerchantNormalizationPrompt(cfg.merchantNormalizationPrompt || '');
      const edits: Record<string, { defaultPrompt: string; systemPrompt: string }> = {};
      cfg.agents.forEach(a => { edits[a.id] = { defaultPrompt: a.defaultPrompt, systemPrompt: a.systemPrompt }; });
      setAgentEdits(edits);
    } catch (err) {
      setError(toUserMessage(err, "We couldn't load the configuration. Please try again."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveGlobal = async () => {
    setSaving(true);
    try {
      await adminApi.ai.updateConfig({
        defaultModel,
        temperature,
        maxTokens,
        dailyTokenCapPerTenant: dailyCap,
        merchantNormalizationModel,
        merchantNormalizationPrompt,
      });
      toast({ title: 'Config saved', status: 'success', duration: 3000 });
    } catch (err) {
      toast({ title: 'Failed to save', description: toUserMessage(err, 'Please try again in a moment.'), status: 'error', duration: 4000 });
    } finally {
      setSaving(false);
    }
  };

  const saveNormalizerSettings = async () => {
    setSavingNormalizer(true);
    try {
      await adminApi.ai.updateConfig({
        merchantNormalizationModel,
        merchantNormalizationPrompt,
      });
      toast({
        title: "Normalizer settings saved",
        status: "success",
        duration: 3000,
      });
    } catch (err) {
      toast({
        title: "Failed to save normalizer settings",
        description: toUserMessage(err, 'Please try again in a moment.'),
        status: "error",
        duration: 4000,
      });
    } finally {
      setSavingNormalizer(false);
    }
  };

  const saveAgentPrompt = async (agentId: string) => {
    const edit = agentEdits[agentId];
    if (!edit) return;
    setSavingAgent(agentId);
    try {
      await adminApi.ai.updateAgentPrompt(agentId, edit);
      toast({ title: `Agent "${agentId}" saved`, status: 'success', duration: 3000 });
    } catch (err) {
      toast({ title: 'Failed to save agent', description: toUserMessage(err, 'Please try again in a moment.'), status: 'error', duration: 4000 });
    } finally {
      setSavingAgent(null);
    }
  };

  const handleCreateAgent = async () => {
    setNewAgentError(null);
    if (!newAgent.id || !newAgent.label || !newAgent.systemPrompt || !newAgent.defaultPrompt) {
      setNewAgentError('All fields are required.');
      return;
    }
    if (!/^[a-z0-9-]+$/.test(newAgent.id)) {
      setNewAgentError('ID must be lowercase letters, digits, and hyphens only.');
      return;
    }
    setCreatingAgent(true);
    try {
      await adminApi.ai.createAgent(newAgent);
      toast({ title: `Agent "${newAgent.id}" created`, status: 'success', duration: 3000 });
      setNewAgent(EMPTY_NEW_AGENT);
      closeNewAgent();
      load();
    } catch (err) {
      setNewAgentError(toUserMessage(err, "We couldn't create the agent. Please try again."));
    } finally {
      setCreatingAgent(false);
    }
  };

  const handleDeleteAgent = async (agentId: string) => {
    if (!window.confirm(`Delete agent "${agentId}"? This cannot be undone.`)) return;
    setDeletingAgent(agentId);
    try {
      await adminApi.ai.deleteAgent(agentId);
      toast({ title: `Agent "${agentId}" deleted`, status: 'success', duration: 3000 });
      load();
    } catch (err) {
      toast({ title: 'Failed to delete agent', description: toUserMessage(err, 'Please try again in a moment.'), status: 'error', duration: 4000 });
    } finally {
      setDeletingAgent(null);
    }
  };

  if (loading) return <VStack spacing={4} align="stretch">{[1,2,3].map(i => <Skeleton key={i} h="80px" borderRadius="lg" />)}</VStack>;
  if (error) return <Alert status="error" borderRadius="md"><AlertIcon /><AlertDescription>{error}</AlertDescription></Alert>;

  return (
    <VStack spacing={6} align="stretch">
      <Box bg={bg} border="1px" borderColor={border} borderRadius="lg" p={5}>
        <HStack justify="space-between" mb={4}>
          <Heading size="sm">Global Settings</Heading>
          <Text fontSize="xs" color={muted}>Updated: {config ? formatDate(config.updatedAt) : '—'}</Text>
        </HStack>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <FormControl>
            <FormLabel fontSize="sm">Default Model</FormLabel>
            <Select value={defaultModel} onChange={e => setDefaultModel(e.target.value)} size="sm">
              {availableModels.map(m => <option key={m} value={m}>{m}</option>)}
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm">Temperature</FormLabel>
            <NumberInput value={temperature} onChange={(_, v) => setTemperature(isNaN(v) ? 0.7 : v)} min={0} max={2} step={0.1} size="sm">
              <NumberInputField />
              <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
            </NumberInput>
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm">Max Tokens</FormLabel>
            <NumberInput value={maxTokens} onChange={(_, v) => setMaxTokens(isNaN(v) ? 1500 : v)} min={256} max={8192} step={256} size="sm">
              <NumberInputField />
              <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
            </NumberInput>
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm">Daily Token Cap / Tenant</FormLabel>
            <NumberInput value={dailyCap} onChange={(_, v) => setDailyCap(isNaN(v) ? 50000 : v)} min={1000} step={1000} size="sm">
              <NumberInputField />
              <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
            </NumberInput>
          </FormControl>
        </SimpleGrid>

        <HStack justify="flex-end" mt={4}>
          <Button leftIcon={<Icon as={MdSave} />} colorScheme="blue" size="sm" onClick={saveGlobal} isLoading={saving}>
            Save
          </Button>
        </HStack>
      </Box>

      <Box bg={neutralSectionBg} border="1px" borderColor={neutralSectionBorder} borderRadius="xl" p={5}>
        <HStack spacing={3} align="start" mb={5}>
          <Icon as={MdAutoFixHigh} boxSize={6} color="blue.500" mt={0.5} />
          <Box flex={1}>
            <Heading size="sm">Merchant Normalizer</Heading>
            <Text fontSize="sm" color={muted} mt={0.5}>
              3-layer flow: Dictionary → Rules → AI fallback only for ambiguous cases.
            </Text>
            <Text fontSize="xs" color={muted} mt={1}>
              Schedule: <Code fontSize="xs">MERCHANT_NORMALIZATION_CRON</Code> / <Code fontSize="xs">MERCHANT_NORMALIZATION_TIMEZONE</Code>
            </Text>
          </Box>
          <Button
            leftIcon={<Icon as={MdSave} />}
            colorScheme="blue"
            variant="outline"
            size="sm"
            onClick={saveNormalizerSettings}
            isLoading={savingNormalizer}
            flexShrink={0}
          >
            Save Settings
          </Button>
        </HStack>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
          <FormControl>
            <FormLabel fontSize="sm">AI Model (fallback only)</FormLabel>
            <Select
              size="sm"
              value={merchantNormalizationModel}
              onChange={e => setMerchantNormalizationModel(e.target.value)}
            >
              {availableModels.map(m => <option key={m} value={m}>{m}</option>)}
            </Select>
            <Text fontSize="xs" color={muted} mt={1}>
              Used only when dictionary and deterministic parsing are not confident.
            </Text>
          </FormControl>
        </SimpleGrid>

        <FormControl mb={4}>
          <FormLabel fontSize="sm">Normalizer System Prompt</FormLabel>
          <AutoResizeTextarea
            value={merchantNormalizationPrompt}
            onChange={e => setMerchantNormalizationPrompt(e.target.value)}
            size="sm"
            fontFamily="mono"
            fontSize="xs"
            minH="120px"
          />
        </FormControl>

        <Box border="1px" borderColor={neutralSectionBorder} borderRadius="lg" p={4} bg={bg}>
          <HStack justify="space-between" mb={3}>
            <Heading size="xs">Cost Estimator</Heading>
            <HStack spacing={2}>
              <Badge colorScheme={selectedCostProfile ? "green" : "orange"}>
                {selectedCostProfile ? "Benchmark available" : "No benchmark for this model"}
              </Badge>
              {selectedCostProfile && (
                <Badge colorScheme="blue" variant="subtle">
                  {selectedCostProfile.reliabilityNote}
                </Badge>
              )}
            </HStack>
          </HStack>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mb={4}>
            <FormControl>
              <FormLabel fontSize="xs">Transactions / day</FormLabel>
              <NumberInput
                size="sm"
                value={estimatedTransactionsPerDay}
                min={1}
                max={10000000}
                step={100}
                onChange={(_, v) => setEstimatedTransactionsPerDay(isNaN(v) ? 10000 : v)}
              >
                <NumberInputField />
                <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
              </NumberInput>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="xs">AI fallback rate %</FormLabel>
              <NumberInput
                size="sm"
                value={estimatedFallbackRatePct}
                min={0}
                max={100}
                step={1}
                onChange={(_, v) => setEstimatedFallbackRatePct(isNaN(v) ? 15 : v)}
              >
                <NumberInputField />
                <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
              </NumberInput>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="xs">Days / month</FormLabel>
              <NumberInput
                size="sm"
                value={estimatedDaysPerMonth}
                min={1}
                max={31}
                step={1}
                onChange={(_, v) => setEstimatedDaysPerMonth(isNaN(v) ? 30 : v)}
              >
                <NumberInputField />
                <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
              </NumberInput>
            </FormControl>
          </SimpleGrid>

          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
            <Box border="1px" borderColor={border} borderRadius="md" p={3}>
              <Text fontSize="xs" color={muted}>Per inference</Text>
              <Text fontSize="sm" fontWeight="bold">{formatUsd(costEstimate.perInference)}</Text>
            </Box>
            <Box border="1px" borderColor={border} borderRadius="md" p={3}>
              <Text fontSize="xs" color={muted}>10,000 inferences</Text>
              <Text fontSize="sm" fontWeight="bold">{formatUsd(costEstimate.per10k)}</Text>
            </Box>
            <Box border="1px" borderColor={border} borderRadius="md" p={3}>
              <Text fontSize="xs" color={muted}>100,000 inferences</Text>
              <Text fontSize="sm" fontWeight="bold">{formatUsd(costEstimate.per100k)}</Text>
            </Box>
            <Box border="1px" borderColor={border} borderRadius="md" p={3}>
              <Text fontSize="xs" color={muted}>1,000,000 inferences</Text>
              <Text fontSize="sm" fontWeight="bold">{formatUsd(costEstimate.per1m)}</Text>
            </Box>
          </SimpleGrid>

          <Divider my={4} />

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
            <Box border="1px" borderColor={border} borderRadius="md" p={3}>
              <Text fontSize="xs" color={muted}>
                Estimated AI calls/day ({estimatedFallbackRatePct}% fallback)
              </Text>
              <Text fontSize="sm" fontWeight="bold">
                {formatTokens(costEstimate.aiCallsPerDay)} calls · {formatUsd(costEstimate.perDay)}
              </Text>
            </Box>
            <Box border="1px" borderColor={border} borderRadius="md" p={3}>
              <Text fontSize="xs" color={muted}>
                Estimated AI calls/month ({estimatedDaysPerMonth} days)
              </Text>
              <Text fontSize="sm" fontWeight="bold">
                {formatTokens(costEstimate.aiCallsPerMonth)} calls · {formatUsd(costEstimate.perMonth)}
              </Text>
            </Box>
          </SimpleGrid>
        </Box>
      </Box>

      {/* ─── Agent Prompts header ───────────────────────────────────── */}
      <HStack justify="space-between">
        <HStack spacing={2}>
          <Icon as={MdSmartToy} boxSize={5} color="purple.400" />
          <Heading size="sm">Agent Prompts</Heading>
          <Badge colorScheme="purple" variant="subtle">{config?.agents.length ?? 0}</Badge>
        </HStack>
        <Button leftIcon={<Icon as={MdAdd} />} colorScheme="purple" size="sm"
          onClick={() => { setNewAgent(EMPTY_NEW_AGENT); setNewAgentError(null); openNewAgent(); }}>
          New Agent
        </Button>
      </HStack>

      {/* ─── Agent search ────────────────────────────────────────────── */}
      <InputGroup size="sm">
        <InputLeftElement pointerEvents="none">
          <Icon as={MdSearch} color={muted} />
        </InputLeftElement>
        <Input
          pl={8}
          placeholder="Search agents..."
          value={agentSearch}
          onChange={e => { setAgentSearch(e.target.value); setSelectedAgentId(null); }}
          borderRadius="md"
        />
      </InputGroup>

      {/* ─── Agent grid ──────────────────────────────────────────────── */}
      {(() => {
        const filtered = (config?.agents ?? []).filter(a =>
          a.label.toLowerCase().includes(agentSearch.toLowerCase()) ||
          a.id.toLowerCase().includes(agentSearch.toLowerCase())
        );
        return (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
            {filtered.map(agent => {
              const isSelected = selectedAgentId === agent.id;
              return (
                <Box
                  key={agent.id}
                  bg={isSelected ? cardHeaderBg : bg}
                  border="1px"
                  borderColor={isSelected ? cardHeaderBorder : border}
                  borderRadius="xl"
                  overflow="hidden"
                  boxShadow={isSelected ? 'md' : 'sm'}
                  _hover={{ boxShadow: 'md', borderColor: cardHeaderBorder }}
                  transition="all 0.15s"
                  cursor="pointer"
                  onClick={() => setSelectedAgentId(isSelected ? null : agent.id)}
                >
                  <HStack px={4} py={3} spacing={3} justify="space-between">
                    <HStack spacing={3}>
                      <Box bg={cardIconBg} borderRadius="lg" p={1.5}>
                        <Icon as={MdSmartToy} boxSize={4} color={cardIconColor} />
                      </Box>
                      <Box>
                        <Text fontWeight="semibold" fontSize="sm" lineHeight="short">{agent.label}</Text>
                        <Badge colorScheme="purple" variant="outline" fontSize="xs" mt={0.5}>{agent.id}</Badge>
                      </Box>
                    </HStack>
                    <Icon
                      as={MdChevronRight}
                      boxSize={4}
                      color={muted}
                      transform={isSelected ? 'rotate(90deg)' : 'none'}
                      transition="transform 0.15s"
                    />
                  </HStack>
                </Box>
              );
            })}
            {filtered.length === 0 && (
              <Text fontSize="sm" color={muted} py={2}>No agents match your search.</Text>
            )}
          </SimpleGrid>
        );
      })()}

      {/* ─── Selected agent edit panel ───────────────────────────────── */}
      {selectedAgentId && (() => {
        const agent = config?.agents.find(a => a.id === selectedAgentId);
        if (!agent) return null;
        const edit = agentEdits[agent.id] ?? { defaultPrompt: agent.defaultPrompt, systemPrompt: agent.systemPrompt };
        return (
          <Box
            bg={bg}
            border="1px"
            borderColor={cardHeaderBorder}
            borderRadius="xl"
            overflow="hidden"
            boxShadow="md"
          >
            {/* Panel header */}
            <Box bg={cardHeaderBg} borderBottom="1px" borderColor={cardHeaderBorder} px={5} py={4}>
              <HStack justify="space-between">
                <HStack spacing={3}>
                  <Box bg={cardIconBg} borderRadius="lg" p={2}>
                    <Icon as={MdSmartToy} boxSize={5} color={cardIconColor} />
                  </Box>
                  <Box>
                    <Text fontWeight="bold" fontSize="md" lineHeight="short">{agent.label}</Text>
                    <Badge colorScheme="purple" variant="outline" fontSize="xs" mt={0.5}>{agent.id}</Badge>
                  </Box>
                </HStack>
                <Button
                  leftIcon={<Icon as={MdDelete} boxSize={4} />}
                  size="sm"
                  colorScheme="red"
                  variant="ghost"
                  isLoading={deletingAgent === agent.id}
                  onClick={() => handleDeleteAgent(agent.id)}
                >
                  Delete
                </Button>
              </HStack>
            </Box>

            {/* Panel body */}
            <Box px={5} py={4}>
              <FormControl mb={4}>
                <FormLabel fontSize="sm" fontWeight="semibold">System Prompt</FormLabel>
                <AutoResizeTextarea
                  value={edit.systemPrompt}
                  onChange={e => setAgentEdits(prev => ({ ...prev, [agent.id]: { ...prev[agent.id], systemPrompt: e.target.value } }))}
                  size="sm"
                  fontFamily="mono"
                  fontSize="xs"
                  borderRadius="md"
                  minH="80px"
                />
              </FormControl>

              <FormControl mb={4}>
                <FormLabel fontSize="sm" fontWeight="semibold">
                  Default Prompt{' '}
                  <Text as="span" fontSize="xs" color={muted} fontWeight="normal">
                    (supports <Code fontSize="xs">{`{{table}}`}</Code>, <Code fontSize="xs">{`{{groupLabel}}`}</Code>)
                  </Text>
                </FormLabel>
                <AutoResizeTextarea
                  value={edit.defaultPrompt}
                  onChange={e => setAgentEdits(prev => ({ ...prev, [agent.id]: { ...prev[agent.id], defaultPrompt: e.target.value } }))}
                  size="sm"
                  fontFamily="mono"
                  fontSize="xs"
                  borderRadius="md"
                  minH="80px"
                />
              </FormControl>

              <HStack justify="flex-end">
                <Button leftIcon={<Icon as={MdSave} />} colorScheme="purple" size="sm"
                  onClick={() => saveAgentPrompt(agent.id)} isLoading={savingAgent === agent.id}>
                  Save Agent
                </Button>
              </HStack>
            </Box>
          </Box>
        );
      })()}

      {/* ─── New Agent Modal ───────────────────────────────────────────── */}
      <Modal isOpen={isNewAgentOpen} onClose={closeNewAgent} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>New Agent</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {newAgentError && (
                <Alert status="error" borderRadius="md" fontSize="sm">
                  <AlertIcon /><AlertDescription>{newAgentError}</AlertDescription>
                </Alert>
              )}
              <SimpleGrid columns={2} spacing={3}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">ID</FormLabel>
                  <Input
                    size="sm" placeholder="e.g. budget-coach" fontFamily="mono"
                    value={newAgent.id}
                    onChange={e => setNewAgent(prev => ({ ...prev, id: e.target.value.toLowerCase() }))}
                  />
                  <Text fontSize="xs" color={muted} mt={1}>Lowercase letters, digits, hyphens only.</Text>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Label</FormLabel>
                  <Input
                    size="sm" placeholder="e.g. Budget Coach"
                    value={newAgent.label}
                    onChange={e => setNewAgent(prev => ({ ...prev, label: e.target.value }))}
                  />
                </FormControl>
              </SimpleGrid>
              <FormControl isRequired>
                <FormLabel fontSize="sm">System Prompt</FormLabel>
                <AutoResizeTextarea
                  size="sm" fontFamily="mono" fontSize="xs"
                  placeholder="You are a financial analyst..."
                  value={newAgent.systemPrompt}
                  onChange={e => setNewAgent(prev => ({ ...prev, systemPrompt: e.target.value }))}
                  minH="80px"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm">
                  Default Prompt{' '}
                  <Text as="span" fontSize="xs" color={muted}>
                    (supports <Code fontSize="xs">{`{{table}}`}</Code>, <Code fontSize="xs">{`{{groupLabel}}`}</Code>)
                  </Text>
                </FormLabel>
                <AutoResizeTextarea
                  size="sm" fontFamily="mono" fontSize="xs"
                  placeholder="Analyze the following {{groupLabel}} data:\n\n{{table}}"
                  value={newAgent.defaultPrompt}
                  onChange={e => setNewAgent(prev => ({ ...prev, defaultPrompt: e.target.value }))}
                  minH="80px"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" size="sm" onClick={closeNewAgent}>Cancel</Button>
            <Button colorScheme="purple" size="sm" leftIcon={<Icon as={MdAdd} />}
              onClick={handleCreateAgent} isLoading={creatingAgent}>
              Create Agent
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

// ─── UsageTab ─────────────────────────────────────────────────────────────────

const UsageTab: React.FC = () => {
  const [tenant, setTenant] = useState<TenantOption | null>(null);
  const [usage, setUsage] = useState<DailyUsage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Hoisted above the two mutually-exclusive empty-state blocks below (they
  // share this same color pair): each was called inline only inside its own
  // `{condition && (...)}` block, and since neither block is guaranteed
  // active on every render (e.g. once usage loads, neither fires), the hook
  // count for this instance varied between renders as tenant/usage change
  // (React: "has detected a change in the order of Hooks").
  const emptyStateColor = useColorModeValue('gray.400', 'gray.500');

  useEffect(() => {
    if (!tenant) { setUsage(null); return; }
    setLoading(true);
    setError(null);
    adminApi.ai.getUsage(tenant.id)
      .then(setUsage)
      .catch(err => setError(toUserMessage(err, 'Please try again in a moment.')))
      .finally(() => setLoading(false));
  }, [tenant]);

  return (
    <VStack spacing={6} align="stretch">
      <TenantSelector value={tenant} onChange={setTenant} />

      {error && <Alert status="error" borderRadius="md"><AlertIcon /><AlertDescription>{error}</AlertDescription></Alert>}

      {loading && <SimpleGrid columns={3} spacing={4}>{[1,2,3].map(i => <Skeleton key={i} h="80px" borderRadius="lg" />)}</SimpleGrid>}

      {!loading && usage && (
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          <StatCard label="Tokens used today" value={formatTokens(usage.tokensUsedToday)} />
          <StatCard label="Requests today" value={usage.requestsToday} color="green.500" />
          <StatCard
            label="Estimated cost today"
            value={`$${usage.estimatedCostToday.toFixed(4)}`}
            color={usage.estimatedCostToday > 0.01 ? 'orange.500' : 'blue.500'}
          />
        </SimpleGrid>
      )}

      {!loading && !usage && tenant && (
        <Box textAlign="center" py={10} color={emptyStateColor}>
          <Icon as={MdBarChart} boxSize={10} mb={2} />
          <Text fontSize="sm">No usage today for <strong>{tenant.name}</strong>.</Text>
        </Box>
      )}

      {!tenant && (
        <Box textAlign="center" py={12} color={emptyStateColor}>
          <Icon as={MdPerson} boxSize={12} mb={3} />
          <Text fontSize="md" fontWeight="medium">Select a tenant</Text>
          <Text fontSize="sm" mt={1}>Choose a tenant above to view their AI usage for today.</Text>
        </Box>
      )}
    </VStack>
  );
};

// ─── ConversationDetailModal ──────────────────────────────────────────────────

const ConversationDetailModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  conversationId: string;
  tenantId: string;
}> = ({ isOpen, onClose, conversationId, tenantId }) => {
  const [detail, setDetail] = useState<ConversationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const userBg = useColorModeValue('blue.50', 'blue.900');
  const assistantBg = useColorModeValue('gray.50', 'gray.700');
  const muted = useColorModeValue('gray.500', 'gray.400');

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setError(null);
    adminApi.ai.getConversation(conversationId, tenantId)
      .then(setDetail)
      .catch(err => setError(toUserMessage(err, "We couldn't load that. Please try again.")))
      .finally(() => setLoading(false));
  }, [isOpen, conversationId, tenantId]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text fontSize="md" fontWeight="semibold">{detail?.title ?? 'Conversation'}</Text>
          {detail && (
            <HStack spacing={2} mt={1}>
              <Badge colorScheme="purple" fontSize="xs">{detail.agentId}</Badge>
              <Badge colorScheme="gray" fontSize="xs">{detail.groupBy}</Badge>
              <Text fontSize="xs" color={muted}>{formatDate(detail.createdAt)}</Text>
            </HStack>
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {loading && <VStack spacing={3}>{[1,2,3].map(i => <Skeleton key={i} h="80px" borderRadius="md" />)}</VStack>}
          {error && <Alert status="error" borderRadius="md"><AlertIcon /><AlertDescription>{error}</AlertDescription></Alert>}
          {detail && (
            <VStack spacing={3} align="stretch">
              {detail.messages.map(msg => (
                <Box key={msg.id} bg={msg.role === 'user' ? userBg : assistantBg} borderRadius="md" p={3}>
                  <HStack justify="space-between" mb={1}>
                    <Badge colorScheme={msg.role === 'user' ? 'blue' : 'green'} fontSize="xs">{msg.role}</Badge>
                    <Text fontSize="xs" color={muted}>{formatDate(msg.createdAt)}</Text>
                  </HStack>
                  <Text fontSize="sm" whiteSpace="pre-wrap" fontFamily={msg.role === 'user' ? 'inherit' : 'mono'}>
                    {msg.content}
                  </Text>
                  {msg.role === 'assistant' && msg.inputTokens != null && (
                    <Text fontSize="xs" color={muted} mt={1}>
                      In: {formatTokens(msg.inputTokens)} · Out: {formatTokens(msg.outputTokens ?? 0)}
                    </Text>
                  )}
                </Box>
              ))}
              {detail.usageLogs.length > 0 && (
                <>
                  <Divider />
                  <Text fontSize="xs" color={muted}>
                    Duration: {detail.usageLogs[0].durationMs}ms · Cost: ${detail.usageLogs[0].costEstimate.toFixed(6)}
                  </Text>
                </>
              )}
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

// ─── ConversationsTab ─────────────────────────────────────────────────────────

const ConversationsTab: React.FC = () => {
  const [tenant, setTenant] = useState<TenantOption | null>(null);
  const [data, setData] = useState<Awaited<ReturnType<typeof adminApi.ai.listConversations>> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [selectedConv, setSelectedConv] = useState<{ id: string; tenantId: string } | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const muted = useColorModeValue('gray.500', 'gray.400');
  const bg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  const rowHover = useColorModeValue('gray.50', 'gray.700');
  // Hoisted above the `!tenant` empty-state block below — see UsageTab's
  // emptyStateColor for the same fix and why.
  const emptyStateColor = useColorModeValue('gray.400', 'gray.500');

  const loadPage = useCallback(async (t: TenantOption, p: number) => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminApi.ai.listConversations(t.id, p, 20);
      setData(result);
    } catch (err) {
      setError(toUserMessage(err, "We couldn't load that. Please try again."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!tenant) { setData(null); setPage(1); return; }
    loadPage(tenant, 1);
    setPage(1);
  }, [tenant, loadPage]);

  const openDetail = (conv: Conversation) => {
    if (!tenant) return;
    setSelectedConv({ id: conv.id, tenantId: tenant.id });
    onOpen();
  };

  return (
    <VStack spacing={6} align="stretch">
      <TenantSelector value={tenant} onChange={setTenant} />

      {error && <Alert status="error" borderRadius="md"><AlertIcon /><AlertDescription>{error}</AlertDescription></Alert>}

      {!tenant && (
        <Box textAlign="center" py={12} color={emptyStateColor}>
          <Icon as={MdPerson} boxSize={12} mb={3} />
          <Text fontSize="md" fontWeight="medium">Select a tenant</Text>
          <Text fontSize="sm" mt={1}>Choose a tenant above to browse their AI conversations.</Text>
        </Box>
      )}

      {tenant && (
        <>
          {data && (
            <HStack justify="space-between">
              <Text fontSize="sm" color={muted}>
                {data.total} conversation{data.total !== 1 ? 's' : ''} total
              </Text>
              <HStack>
                <Button size="xs" variant="outline"
                  isDisabled={page <= 1}
                  onClick={() => { const p = page - 1; setPage(p); loadPage(tenant, p); }}>
                  Prev
                </Button>
                <Text fontSize="xs" color={muted}>Page {page}</Text>
                <Button size="xs" variant="outline"
                  isDisabled={!data || page * data.pageSize >= data.total}
                  onClick={() => { const p = page + 1; setPage(p); loadPage(tenant, p); }}>
                  Next
                </Button>
              </HStack>
            </HStack>
          )}

          <Box bg={bg} border="1px" borderColor={border} borderRadius="lg" overflow="hidden">
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Title</Th>
                  <Th>Agent</Th>
                  <Th>Group</Th>
                  <Th isNumeric>Tokens</Th>
                  <Th>Date</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {loading
                  ? [1,2,3,4,5].map(i => (
                    <Tr key={i}>{[1,2,3,4,5,6].map(j => <Td key={j}><Skeleton h="16px" /></Td>)}</Tr>
                  ))
                  : !data || data.conversations.length === 0
                  ? (
                    <Tr>
                      <Td colSpan={6}>
                        <Text fontSize="sm" color={muted} py={4} textAlign="center">
                          No conversations for {tenant.name}
                        </Text>
                      </Td>
                    </Tr>
                  )
                  : data.conversations.map(conv => {
                    const log = conv.usageLogs[0];
                    const totalTokens = log ? log.inputTokens + log.outputTokens : 0;
                    return (
                      <Tr key={conv.id} _hover={{ bg: rowHover }} cursor="pointer" onClick={() => openDetail(conv)}>
                        <Td maxW="260px">
                          <Text fontSize="xs" noOfLines={2}>{conv.title}</Text>
                        </Td>
                        <Td><Badge colorScheme="purple" fontSize="xs">{conv.agentId}</Badge></Td>
                        <Td><Badge colorScheme="gray" fontSize="xs">{conv.groupBy}</Badge></Td>
                        <Td isNumeric><Text fontSize="xs">{totalTokens > 0 ? formatTokens(totalTokens) : '—'}</Text></Td>
                        <Td><Text fontSize="xs" color={muted}>{formatDate(conv.createdAt)}</Text></Td>
                        <Td>
                          <Tooltip label="View conversation">
                            <IconButton aria-label="View" icon={<Icon as={MdOpenInNew} />} size="xs" variant="ghost" onClick={e => { e.stopPropagation(); openDetail(conv); }} />
                          </Tooltip>
                        </Td>
                      </Tr>
                    );
                  })}
              </Tbody>
            </Table>
          </Box>
        </>
      )}

      {selectedConv && (
        <ConversationDetailModal
          isOpen={isOpen}
          onClose={onClose}
          conversationId={selectedConv.id}
          tenantId={selectedConv.tenantId}
        />
      )}
    </VStack>
  );
};

// ─── AdminAiPage ──────────────────────────────────────────────────────────────

export const AdminAiPage: React.FC = () => {
  const muted = useColorModeValue('gray.500', 'gray.400');

  return (
    <VStack spacing={6} align="stretch">
      <HStack spacing={3}>
        <Icon as={MdSmartToy} color="purple.400" boxSize={6} />
        <Box>
          <Heading size="lg">AI Analysis</Heading>
          <Text fontSize="sm" color={muted}>
            Configure analysis models, merchant normalizer settings, token caps, and audit tenant conversations.
          </Text>
        </Box>
      </HStack>

      <Tabs colorScheme="purple" isLazy>
        <TabList>
          <Tab fontSize="sm">Config</Tab>
          <Tab fontSize="sm">Usage</Tab>
          <Tab fontSize="sm">Conversations</Tab>
        </TabList>

        <TabPanels>
          <TabPanel px={0}><ConfigTab /></TabPanel>
          <TabPanel px={0}><UsageTab /></TabPanel>
          <TabPanel px={0}><ConversationsTab /></TabPanel>
        </TabPanels>
      </Tabs>
    </VStack>
  );
};

export default AdminAiPage;
